import { useMemo, useState } from 'react';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';
import { paginationDefaultValues } from './constants.js';
import { PaginationDefaultValue } from './types.js';
export const usePagination = (limit) => {
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(paginationDefaultValues[limit ?? PaginationDefaultValue.LG]);
    // Starts at 0 and is fed by the owner via `setTotal` once the paginated response arrives.
    const [total, setTotal] = useState(0);
    // The displayed window is clamped to the last existing page, so it stays valid (`from <= to`)
    // while `page` is momentarily out of range after `total` shrinks.
    const maxPage = total === 0 ? 0 : Math.ceil(total / perPage) - 1;
    const safePage = Math.min(page, maxPage);
    const from = total === 0 ? 0 : safePage * perPage + 1;
    const to = Math.min(total, (safePage + 1) * perPage);
    const hasPrev = page > 0;
    const hasNext = page < maxPage;
    // Changing the page size sends the reader back to the first page, because the old page index
    // addresses different rows once they are redistributed. An unchanged size is a no-op — `Select`
    // re-fires `onChange` for the option that is already selected.
    const updatePerPage = useCallbackRef((value) => {
        if (value === perPage) {
            return;
        }
        setPerPage(value);
        setPage(0);
    });
    // Every page write is clamped to `0..maxPage`, so no caller can address a page that does not exist.
    const updatePage = useCallbackRef((value) => {
        setPage(Math.max(0, Math.min(value, maxPage)));
    });
    const nextPage = useCallbackRef(() => {
        updatePage(page + 1);
    });
    const prevPage = useCallbackRef(() => {
        updatePage(page - 1);
    });
    // A `total` that no longer covers the current page (e.g. the last row on it was deleted) sends the
    // reader to the last existing page. A total of 0 is skipped: it also means "no response yet".
    const updateTotal = useCallbackRef((value) => {
        setTotal(value);
        const nextMaxPage = value === 0 ? 0 : Math.ceil(value / perPage) - 1;
        if (value > 0 && page > nextMaxPage) {
            setPage(nextMaxPage);
        }
    });
    // Shifts the total when a row is added or removed without a refetch, so the owner states the change
    // instead of writing a recomputed number back.
    const changeTotal = useCallbackRef((delta) => {
        updateTotal(Math.max(0, total + delta));
    });
    return useMemo(() => ({
        page,
        setPage: updatePage,
        nextPage,
        prevPage,
        hasNext,
        hasPrev,
        perPage,
        setPerPage: updatePerPage,
        from,
        to,
        total,
        setTotal: updateTotal,
        changeTotal,
    }), [page, updatePage, nextPage, prevPage, hasNext, hasPrev, perPage, updatePerPage, from, to, total, updateTotal, changeTotal]);
};
